import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import { LoadingSpinner } from '@/components/LoadingSpinner'

interface ImageUploadProps {
  currentImageUrl?: string | null
  onImageUpload: (url: string, path: string) => void
  onImageRemove: () => void
  bucket?: string
  maxSizeMB?: number
  disabled?: boolean
}

export function ImageUpload({
  currentImageUrl,
  onImageUpload,
  onImageRemove,
  bucket = 'menu-items',
  maxSizeMB = 5,
  disabled = false,
}: ImageUploadProps) {
  const { t } = useTranslation()
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setError(null)

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('validation.invalidImageType') || 'Please select an image file')
      return
    }

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024)
    if (fileSizeMB > maxSizeMB) {
      setError(t('validation.imageTooLarge') || `Image size must be less than ${maxSizeMB}MB`)
      return
    }

    try {
      setIsUploading(true)

      // Create preview
      const objectUrl = URL.createObjectURL(file)
      setPreviewUrl(objectUrl)

      // Generate unique file name
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
      const filePath = fileName

      // Upload to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        })

      if (uploadError) {
        throw uploadError
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path)

      // Call the parent callback with the URL and path
      onImageUpload(publicUrl, data.path)

      // Clean up object URL
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error('Error uploading image:', err)
      setError(t('error.imageUploadFailed') || 'Failed to upload image. Please try again.')
      setPreviewUrl(currentImageUrl || null)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = () => {
    setPreviewUrl(null)
    setError(null)
    onImageRemove()

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {previewUrl ? (
        <div className="relative inline-block">
          <div className="relative w-48 h-48 rounded-lg overflow-hidden border-2 border-border bg-muted">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-cover"
            />
            {isUploading && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            )}
          </div>
          {!disabled && !isUploading && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-8 w-8 rounded-full shadow-lg"
              onClick={handleRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        <div
          onClick={handleButtonClick}
          className={`w-48 h-48 rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer bg-muted/30 ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isUploading ? (
            <LoadingSpinner />
          ) : (
            <>
              <ImageIcon className="w-12 h-12 text-muted-foreground" />
              <div className="text-center px-4">
                <p className="text-sm font-medium text-muted-foreground">
                  {t('menu.uploadImage') || 'Upload Image'}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {t('menu.imageHint') || `Max ${maxSizeMB}MB`}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {!previewUrl && !disabled && (
        <Button
          type="button"
          variant="outline"
          onClick={handleButtonClick}
          disabled={isUploading}
          className="w-48"
        >
          <Upload className="w-4 h-4 me-2" />
          {t('menu.selectImage') || 'Select Image'}
        </Button>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
